import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';

import Share from '@deliveroo/icon-set/react/ShareIos';
import P from '@deliveroo/consumer-component-library/components/P';
import Button from '@deliveroo/consumer-component-library/components/Button';
import Icon from '@deliveroo/consumer-component-library/components/Icon';
import LoadingIndicator from '@deliveroo/consumer-component-library/components/LoadingIndicator';
import Modal from '@deliveroo/consumer-component-library/components/Modal';

import i18n from '../../utils/i18n';

import ReferralCodeInput from '../ReferralCodeInput';

import styles from './styles.scss';

let timer;
/**
 * A trigger component to open the OrderShareModal. Used on the Order Status page.
 */
class OrderShareModalButton extends Component {
  constructor(props) {
    super(props);

    this.state = {
      modalOpen: false,
      shareLink: '',
    };
  }

  componentWillUnmount() {
    window.clearTimeout(timer);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.sharingToken !== this.props.sharingToken) {
      this.updateShareLink();
    }
  }

  updateShareLink() {
    let link;

    if (this.props.sharingShortUrl) {
      link = this.props.sharingShortUrl;
    } else {
      /* get url without parameters */
      const url = window.location.href.split('?')[0];
      link = `${url}?sharing_token=${this.props.sharingToken}`;
    }

    this.setState({
      shareLink: link,
    });
  }

  onModalOpen = () => {
    this.updateShareLink();
    this.props.requestOrderStatusShare();
    this.props.onClickedShareTracker();
    this.setState({
      modalOpen: true,
    });
  };

  onModalClose = () => {
    this.setState({
      modalOpen: false,
    });
  };

  onCopyClick = () => {
    this.props.onCopiedShareTrackerLink();
  };

  onTryAgain = () => {
    this.props.requestOrderStatusShare();
  };

  render() {
    const { loading, error, appElement, mobileView } = this.props;

    const { modalOpen, shareLink } = this.state;

    let modalContents;
    let modalFooter;

    if (loading) {
      modalContents = (
        <div className={styles.loading}>
          <LoadingIndicator color="teal" size={18} spacing={[]} />
        </div>
      );
    } else if (error) {
      modalContents = (
        <div className={styles.error}>
          <P isBold spacing="small 0">
            {i18n.gettext('share_order_tracker_modal.error_title.text')}
          </P>
          <P spacing="small 0">{i18n.gettext('share_order_tracker_modal.error_advice.text')}</P>
        </div>
      );

      modalFooter = () => (
        <Button type="primary" onClick={this.onTryAgain} isBlock>
          {i18n.gettext('share_order_tracker_modal.error_action.button')}
        </Button>
      );
    } else if (shareLink) {
      modalContents = (
        <Fragment>
          <ReferralCodeInput
            copiedLabel={i18n.gettext('share_order_tracker_modal.copied.button')}
            copyLabel={i18n.gettext('share_order_tracker_modal.copy.button')}
            copyValue={shareLink}
            displayValue={shareLink.split('//')[1]}
            label={i18n.gettext('share_order_tracker_modal.input_label.text')}
            onCopyClick={this.onCopyClick}
          />

          <P size="x-small" color="grey" adjacentVerticalSpacing="small">
            {i18n.gettext('share_order_tracker_modal.input_help.text')}
          </P>
        </Fragment>
      );
    }

    return (
      <Fragment>
        {mobileView ? (
          <button
            onClick={this.onModalOpen}
            className={styles['mobile-button']}
            tabIndex="0"
            type="button"
            aria-label={i18n.gettext('share_order_tracker_modal.title.text')}
            title={i18n.gettext('share_order_tracker_modal.title.text')}
          >
            <span className={styles['mobile-button-icon']}>
              <Icon component={Share} size={24} />
            </span>
          </button>
        ) : (
          <Button onClick={this.onModalOpen} icon={Share} isBlock type="secondary">
            {i18n.gettext('order_status_page.open_share_tracker_modal.button')}
          </Button>
        )}

        {modalContents && (
          <Modal
            id="share-modal"
            appElement={appElement}
            type="fullscreen"
            isOpen={modalOpen}
            onClose={this.onModalClose}
            closeButtonLabel={i18n.gettext('closeButton.label')}
            headerTitle={i18n.gettext('share_order_tracker_modal.title.text')}
            renderFooter={modalFooter}
          >
            {modalContents}
          </Modal>
        )}
      </Fragment>
    );
  }
}

OrderShareModalButton.propTypes = {
  loading: PropTypes.bool.isRequired,
  error: PropTypes.shape({
    message: PropTypes.string,
    statusCode: PropTypes.number,
  }),
  /**
   * Allows you to specify the portion of your app that should be hidden
   * (via aria-hidden) to prevent assistive technologies such as screenreaders
   * from reading content outside of the content of your modal.
   * Also added so that it renders properly in Lemon.
   */
  appElement: PropTypes.string,
  /** The sharing token for the sharing link */
  sharingToken: PropTypes.string,
  /** A shortened URL for the shared order status page */
  sharingShortUrl: PropTypes.string,
  /** Function called to enable share in the backend */
  requestOrderStatusShare: PropTypes.func.isRequired,
  /** If on mobile view */
  mobileView: PropTypes.bool,
  /** Function to track button click */
  onClickedShareTracker: PropTypes.func,
  /** Function to track share tracker link is copied */
  onCopiedShareTrackerLink: PropTypes.func,
};

OrderShareModalButton.defaultProps = {
  appElement: '#__next',
  error: null,
  mobileView: false,
  sharingToken: '',
  sharingShortUrl: undefined,
  onClickedShareTracker: () => {},
  onCopiedShareTrackerLink: () => {},
};

export default OrderShareModalButton;
